import { useEffect, useState, useRef } from 'react';
import GloLayout from 'lowcode/common/GloLayout';
import { StatGetDeviceId } from 'eco-design-micro/EcoStat/config';
import SingleProduct from './components/SingleProduct';
import BundleProduct from './components/BundleProduct';
import { getGloAIProductList } from '@/appSetting/get-global-ai/getGloAIProductList';
import { getAIUserInfo } from '@/service/vip';
import generateTraceId from '@/utils/singUtil/generateTraceId';
import getLocal from '@/app/utils/getLocal';
import { loginUser } from '@/utils/cookie';
import { times } from 'lodash';

import './index.scss';
import { productRes, bundlesRes, productRes2 } from '@/app/eflayout/Header/DropBox/DropBoxSearch/mock';
import { getIsLogin } from '@/utils/cookie';
import { queryShopifyProductList } from '@/service/vip';
import gtm from '@/utils/gtm';
import { ItfEcoCartInventoryPolicy } from 'eco-design/elements/eco-cart/type.d';

// 定义产品类型
type ProductItem = {
    productId: string;
    variantId: string;
    quantity: number;
    price: string;
    compareAtPrice: string;
    img: string;
    variantImg: string;
    productTitle: string;
    skuTitle: string;
    inventoryPolicy?: ItfEcoCartInventoryPolicy;
    incenvrotyQuantity?: number;
    variantDescription: string;
    handle: string;
    specific: string;
    itemType?: string;
};

// 定义Bundle类型
type BundleItem = {
    itemId: string;
    itemType: string;
    retrieveId: string;
    score: number;
    variants: ProductItem[];
};

interface ContentProps {
    id: string;
    data: {
        title?: string;
    };
}

export default function Content({ id, data }: ContentProps) {
    const { title } = data;
    const [items, setItems] = useState<ProductItem[]>([]);
    const [bundles, setBundles] = useState<BundleItem[]>([]);
    const [showType, setShowType] = useState('');
    const [list, setList] = useState<any[]>([]);
    const { locale = '' } = getLocal();
    const userId = loginUser()?.user?.userId;
    const [hasTracked, setHasTracked] = useState(false);
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
    const sectionRef = useRef<HTMLDivElement>(null);

    const prefixCls = 'temp-member-product-ai-recommend';

    const handleQueryVipData = async (sessionId: string | any) => {
        let infoData = null;

        if (!!getIsLogin()) {
            const userInfoRes = await getAIUserInfo(locale as string);
            if (userInfoRes?.code === '0') {
                infoData = userInfoRes?.data;
            }
            await handleQueryRecommendList({ ...infoData, sessionId });
        } else {
            await handleQueryRecommendList({ sessionId });
        }
    };

    const handleQueryRecommendList = async (data?: any) => {
        try {
            const deviceId = await StatGetDeviceId({});

            const params = {
                triggerPage: 'Brand Member Recommend',
                tid: generateTraceId(),
                userId,
                deviceId: deviceId || '',
                ...data,
            };
            const productRes = await getGloAIProductList(params, locale);
            if (productRes?.code === '0') {
                let finalList = productRes?.data?.items;
                if (finalList?.length > 0) {
                    if (productRes?.data?.items?.length > 12) {
                        finalList = productRes?.data?.items?.slice(0, 12);
                    }
                    setList(finalList || []);
                    setShowType(finalList[0]?.itemType);
                } else {
                    // TODO: 这里需要修改成 shopify 单品的接口
                    handleQueryProductList();
                }
            } else {
                handleQueryProductList();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleQueryProductList = async () => {
        const res = await queryShopifyProductList(locale);
        if (res?.code === '0') {
            const productList = res?.data || [];
            setShowType('shopifyProduct');
            handleFormatProductList(productList);
        } else {
        }
    };

    const handleFormatProductList = (productList: any[]) => {
        const formatList: any[] = [];
        productList.forEach((item: any) => {
            formatList.push({
                itemId: item?.variantId,
                itemType: 'shopifyProduct',
                variants: [item],
            });
        });
        setList(formatList?.slice(0, 12));
    };

    const handleTrackList = (list: any[], type: string) => {
        let initProductTitle = '';
        if (type === 'shopifyProduct') {
            initProductTitle = list?.map((item) => item?.variants[0]?.productTitle).join('_');
        }

        if (type === 'shopifyBundle') {
            initProductTitle = list?.flatMap((item) => item?.variants?.map((variant: any) => variant?.productTitle) || []).join('_');
        }

        return initProductTitle;
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasTracked) {
                    const initProductTitle = handleTrackList(list, showType);
                    const timeout = setTimeout(() => {
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'view_list',
                            product_name: initProductTitle || 'none',
                            position: 0,
                            list_type: 'AI Brand Member Recommend',
                            list_title: title || 'Recommended EcoFlow Products',
                            display_type: showType === 'shopifyProduct' ? 'Shopify Product Page' : 'Recommend Bundles',
                        });

                        setHasTracked(true); // 避免重复埋点
                    }, 5000);
                    setTimer(timeout);
                } else {
                    // 如果离开视口前未满 5s，则清除定时器
                    if (timer) {
                        clearTimeout(timer);
                        setTimer(null);
                    }
                }
            },
            {
                threshold: 0.5, // 超过一半面积进入视口才触发
            },
        );

        const target = sectionRef.current;
        if (target) {
            observer.observe(target);
        }

        return () => {
            if (target) {
                observer.unobserve(target);
            }
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [hasTracked, list]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const session = sessionStorage?.getItem('ef_search_ai_session_id');
            handleQueryVipData(session);
        }
        setItems(productRes?.items || []);
        setBundles(bundlesRes?.data?.items || []);
    }, []);

    return (
        <GloLayout id={id} className={`temp-member-product-scene ${prefixCls}  temp-hide`}>
            <div className={`${prefixCls}-head ef-content ${title ? '' : ` ${prefixCls}-head-hide`}`}>
                <div className={`${prefixCls}-head-title`}>{title}</div>
            </div>

            <div ref={sectionRef}>
                {showType === 'shopifyProduct' && <SingleProduct data={list} />}
                {showType === 'shopifyBundle' && <BundleProduct data={list} />}
            </div>
        </GloLayout>
    );
}
